// Options des filtres du Catalogue v2 (option B validee le 15/10). Cles et
// libelles repris de l'API, qui fait foi : api/src/lib/catalogueAudience.ts
// (POUR_QUI_LABEL, OCCASION_V2_LABEL), api/src/lib/productClassification.ts
// (RAYONS, SOUS_RAYON_PARENT), api/src/config/guidedSelection.ts (libelles des
// sous-rayons) et api/src/lib/catalogueFacets.ts (BUDGETS). Un test cote API
// verifie que ces listes restent identiques (catalogueFilterOptions.test.ts).

export const POUR_QUI = ['0-1', '1-3', '3-6', '6-10', '10-14', 'ado', 'adulte'] as const;
export type PourQui = (typeof POUR_QUI)[number];

export const POUR_QUI_LABEL: Record<PourQui, string> = {
  '0-1': 'Bébé',
  '1-3': '1 à 3 ans',
  '3-6': '3 à 6 ans',
  '6-10': '6 à 10 ans',
  '10-14': '10 à 14 ans',
  ado: 'Ado',
  adulte: 'Adulte',
};

export const OCCASIONS = ['naissance', 'bapteme', 'anniversaire', 'noel', 'mariage', 'pacs', 'cremaillere', 'communion', 'retraite'] as const;
export type Occasion = (typeof OCCASIONS)[number];

export const OCCASION_LABEL: Record<Occasion, string> = {
  naissance: 'Naissance',
  bapteme: 'Baptême',
  anniversaire: 'Anniversaire',
  noel: 'Noël',
  mariage: 'Mariage',
  pacs: 'Pacs',
  cremaillere: 'Crémaillère',
  communion: 'Communion',
  retraite: 'Retraite, départ',
};

export const BUDGETS = ['moins-de-20', '20-50', '50-100', 'plus-de-100'] as const;
export type Budget = (typeof BUDGETS)[number];

export const BUDGET_LABEL: Record<Budget, string> = {
  'moins-de-20': 'Moins de 20\u00A0€',
  '20-50': '20 à 50\u00A0€',
  '50-100': '50 à 100\u00A0€',
  'plus-de-100': 'Plus de 100\u00A0€',
};

export const RAYONS = ['bebe', 'jouets', 'mode', 'bijoux', 'beaute', 'maison', 'cuisine', 'gourmandises', 'high-tech', 'sport', 'culture', 'voyages', 'cartes-cadeaux'] as const;
export type Rayon = (typeof RAYONS)[number];

export const RAYON_LABEL: Record<Rayon, string> = {
  bebe: 'Bébé & puériculture',
  jouets: 'Jouets & jeux',
  mode: 'Mode & accessoires',
  bijoux: 'Bijoux & montres',
  beaute: 'Beauté & bien-être',
  maison: 'Maison & déco',
  cuisine: 'Cuisine & art de la table',
  gourmandises: 'Gourmandises & épicerie fine',
  'high-tech': 'High-tech & jeux vidéo',
  sport: 'Sport & plein air',
  culture: 'Livres & culture',
  voyages: 'Voyages & expériences',
  'cartes-cadeaux': 'Cartes cadeaux',
};

/** Familles de la sous-page Rayons (maquette Cat-4c), reprises des familles du catalogue. */
export const RAYON_GROUPS: ReadonlyArray<{ title: string; rayons: readonly Rayon[] }> = [
  { title: 'Cadeaux & lifestyle', rayons: ['maison', 'cuisine', 'gourmandises', 'mode', 'beaute', 'bijoux', 'bebe'] },
  { title: 'Tech & loisirs', rayons: ['high-tech', 'jouets', 'sport'] },
  { title: 'Culture & autres', rayons: ['culture', 'voyages', 'cartes-cadeaux'] },
];

/** Sous-rayons (rayons de la liste guidee) : libelle et rayon parent. */
export const SOUS_RAYONS: Record<string, { label: string; rayon: Rayon }> = {
  sommeil: { label: 'Sommeil', rayon: 'bebe' },
  repas: { label: 'Repas', rayon: 'bebe' },
  balade: { label: 'Balade', rayon: 'bebe' },
  eveil: { label: 'Éveil', rayon: 'bebe' },
  bain: { label: 'Bain', rayon: 'bebe' },
  souvenirs: { label: 'Souvenirs', rayon: 'bebe' },
  vetements: { label: 'Vêtements', rayon: 'mode' },
  construction: { label: 'Construction', rayon: 'jouets' },
  creatif: { label: 'Créatif', rayon: 'jouets' },
  imaginaire: { label: 'Imaginaire', rayon: 'jouets' },
  'jeux-societe': { label: 'Jeux de société', rayon: 'jouets' },
  jeux: { label: 'Jeux', rayon: 'jouets' },
  'plein-air': { label: 'Plein air', rayon: 'sport' },
  sport: { label: 'Sport', rayon: 'sport' },
  lecture: { label: 'Livres', rayon: 'culture' },
  musique: { label: 'Musique', rayon: 'culture' },
  cuisine: { label: 'Cuisine', rayon: 'cuisine' },
  table: { label: 'Arts de la table', rayon: 'cuisine' },
  gourmandises: { label: 'Gourmandises', rayon: 'gourmandises' },
  linge: { label: 'Linge de maison', rayon: 'maison' },
  deco: { label: 'Déco', rayon: 'maison' },
  jardin: { label: 'Jardin', rayon: 'maison' },
  'bien-etre': { label: 'Bien-être', rayon: 'beaute' },
  'high-tech': { label: 'High-tech', rayon: 'high-tech' },
  experiences: { label: 'Expériences', rayon: 'voyages' },
  mode: { label: 'Mode', rayon: 'mode' },
  bijoux: { label: 'Bijoux', rayon: 'bijoux' },
};

// Anciens liens (?univers=&subcat=, pages themes et favoris) : univers vers
// rayon, comme le classement cote API (UNIVERSE_RAYON) ; sous-categories
// historiques vers leur equivalent v2 quand il est sans ambiguite, sinon le
// rayon seul.
export const LEGACY_UNIVERSE_RAYON: Record<string, Rayon> = {
  'bebe-enfant': 'bebe',
  maison: 'maison',
  mode: 'mode',
  beaute: 'beaute',
  bijoux: 'bijoux',
  voyage: 'voyages',
  'cartes-cadeaux': 'cartes-cadeaux',
  'high-tech': 'high-tech',
  'jeux-video': 'high-tech',
  'informatique-bureau': 'high-tech',
  'sport-loisirs': 'sport',
  'bricolage-jardin': 'maison',
  // 'auto-moto' : sans rayon depuis le 15/10 (aucun des 13 ne convient), l'ancien lien n'impose plus de rayon.
  livres: 'culture',
};

export interface LegacySubcatTarget {
  rayon?: Rayon;
  sousRayon?: string;
  pourQui?: PourQui;
}

/** Cle : `univers/subcat`. */
export const LEGACY_SUBCAT: Record<string, LegacySubcatTarget> = {
  'maison/cuisine': { rayon: 'cuisine' },
  'maison/chambre': { rayon: 'maison', sousRayon: 'linge' },
  'maison/decoration': { rayon: 'maison', sousRayon: 'deco' },
  'maison/jardin': { rayon: 'maison', sousRayon: 'jardin' },
  'bebe-enfant/jouets': { rayon: 'jouets' },
  'bebe-enfant/bebe': { rayon: 'bebe', pourQui: '0-1' },
  'bebe-enfant/mobilier-chambre': { rayon: 'bebe', sousRayon: 'sommeil' },
  'bebe-enfant/soin': { rayon: 'bebe', sousRayon: 'bain' },
  'bebe-enfant/ado': { pourQui: 'ado' },
  'mode/enfant': { rayon: 'mode', sousRayon: 'vetements' },
  'voyage/activites': { rayon: 'voyages', sousRayon: 'experiences' },
};

export const isPourQui = (v: unknown): v is PourQui => typeof v === 'string' && (POUR_QUI as readonly string[]).includes(v);
export const isOccasion = (v: unknown): v is Occasion => typeof v === 'string' && (OCCASIONS as readonly string[]).includes(v);
export const isBudget = (v: unknown): v is Budget => typeof v === 'string' && (BUDGETS as readonly string[]).includes(v);
export const isRayon = (v: unknown): v is Rayon => typeof v === 'string' && (RAYONS as readonly string[]).includes(v);
export const isSousRayon = (v: unknown): v is string => typeof v === 'string' && Object.prototype.hasOwnProperty.call(SOUS_RAYONS, v);

/** « Léa Nature » -> « LEA NATURE » : tri et recherche sans accents ni apostrophes. */
export function foldForSort(name: string): string {
  return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[\u2019']/g, '').toUpperCase().trim();
}

/** Marques de A a Z, groupees par initiale (chiffres et symboles sous « # »). */
export function groupByLetter<T extends { name: string }>(items: readonly T[]): { letter: string; items: T[] }[] {
  const sorted = [...items].sort((a, b) => foldForSort(a.name).localeCompare(foldForSort(b.name), 'fr'));
  const groups: { letter: string; items: T[] }[] = [];
  for (const item of sorted) {
    const first = foldForSort(item.name)[0] ?? '#';
    const letter = /[A-Z]/.test(first) ? first : '#';
    const last = groups[groups.length - 1];
    if (last && last.letter === letter) last.items.push(item);
    else groups.push({ letter, items: [item] });
  }
  return groups;
}

/** Format du montant libre : « 20 € », « 20 à 50 € », « Jusqu'à 50 € ». */
export function priceRangeLabel(min: number | null, max: number | null): string {
  if (min != null && max != null) return `${min} à ${max}\u00A0€`;
  if (min != null) return `Dès ${min}\u00A0€`;
  if (max != null) return `Jusqu'à ${max}\u00A0€`;
  return '';
}

/** Bouton de la feuille : « Voir 54 idées », « Voir 1 idée », « Aucune idée ». */
export function seeIdeasLabel(total: number | null): string {
  if (total == null) return 'Voir les idées';
  if (total === 0) return 'Aucune idée';
  return `Voir ${total.toLocaleString('fr-FR')} idée${total > 1 ? 's' : ''}`;
}
